import { datadogRum, type RumEvent, type RumResourceEvent } from '@datadog/browser-rum';

/**
 * NOTE Datadog site 설정
 * @see https://docs.datadoghq.com/getting_started/site/
 */
export const DATA_DOG_SITE = 'datadoghq.com';

/** NOTE Datadog RUM context group 식별자 */
export const DATA_DOG_GROUP = 'snd-frontend';

/** NOTE Datadog RUM error source 식별자 */
export const DATA_DOG_SOURCES = {
  RUM_INIT: 'datadogRumInit',
  RUM_ADD_ERROR: 'datadogRumAddError',
} as const;

export const DATA_DOG_SERVICES = {
  SEARCH: 'search-frontend',
  CATEGORY: 'category-frontend',
  COUPON: 'coupon-frontend',
  PRODUCT_DETAIL: 'pdp-frontend',
  BRAND: 'brand-frontend',
  MAIN: 'home-frontend',
  RECOMMEND: 'recommend-frontend',
  RELEASE: 'release-frontend',
  RANKING: 'ranking-frontend',
  MENU: 'menu-frontend',
  PLUS_DELIVERY: 'plusdelivery-frontend',
  LIKE: 'like-frontend',
  GIFT: 'gift-frontend',
  TIMESALE: 'timesale-frontend',
} as const;

/** NOTE Datadog RUM team 식별자 */
export const DATA_DOG_TEAMS = {
  DISPLAY: 'fe-display',
} as const;

export type DataDogTeam = (typeof DATA_DOG_TEAMS)[keyof typeof DATA_DOG_TEAMS];

export type DataDogService = (typeof DATA_DOG_SERVICES)[keyof typeof DATA_DOG_SERVICES];

const BASE_CONTEXT = {
  url: window.location.href,
  group: DATA_DOG_GROUP,
  version: import.meta.env.VITE_APP_VERSION,
};

/** Phase 1: 확실한 노이즈 (브라우저 API, 크로스 오리진, 비정상 reject) */
const IGNORE_MESSAGE_PATTERNS_PHASE1 = [
  'ResizeObserver loop',
  'Script error.',
  'Non-Error promise rejection captured',
  'Non-Error exception captured',
] as const;

/**
 * Phase 2: 순수 노이즈 드롭 대상
 * - 광고 차단/리포팅 SDK 자체 실패/비디오 SDK 내부 오류 등 행동 가능한 정보가 없는 메시지
 * - 네트워크 "원인 구분"에 해당하는 메시지는 아래 NETWORK_ERROR_PATTERNS로 이동 — 드롭하지 않고 카테고리 태그 후 수집
 */
const IGNORE_MESSAGE_PATTERNS_PHASE2 = [
  'ERR_BLOCKED_BY_CLIENT',
  '[heathrow] Error reporting failed',
  'Failed to play video',
] as const;

/**
 * 네트워크 이슈 세분화 카테고리
 * - Datadog RUM 이벤트 @context.networkCategory 로 수집되어 facet 그룹핑에 사용됨
 */
export const NETWORK_CATEGORY = {
  OK: 'ok',
  HTTP_4XX: 'http-4xx',
  HTTP_5XX: 'http-5xx',
  /** DNS phase duration 임계치 초과 — 도메인 해석 지연/실패 */
  DNS_FAILURE: 'dns-failure',
  /** connect phase duration 임계치 초과 — 커넥션 풀 고갈/keep-alive 재사용 실패 의심 */
  CONNECT_EXHAUSTION: 'connect-exhaustion',
  /** ssl phase 실패 (handshake 중단) */
  TLS_ERROR: 'tls-error',
  /** first_byte (TTFB) 임계치 초과 — 백엔드 처리 지연 */
  SERVER_SLOW: 'server-slow',
  /** download phase 임계치 초과 — 큰 응답/대역폭 제약 */
  DOWNLOAD_SLOW: 'download-slow',
  /** 사용자 취소/AbortController — 장애가 아닌 UX 신호 */
  CLIENT_ABORT: 'client-abort',
  /** 네트워크 단절 (오프라인, DNS 아예 실패 등) */
  OFFLINE: 'offline',
} as const;

export type NetworkCategory = (typeof NETWORK_CATEGORY)[keyof typeof NETWORK_CATEGORY];

/**
 * Phase duration 임계치 (ms 단위)
 * NOTE 배포 후 1~2주 Datadog 지표를 보고 재튜닝 — 초기값은 경험적 추정
 */
const CONNECT_EXHAUSTION_MS = 1_000;
const SERVER_SLOW_MS = 3_000;
const DNS_FAILURE_MS = 3_000;

/** ms → ns (Datadog RUM phase duration 단위 변환) */
const msToNs = (ms: number): number => ms * 1_000_000;

/**
 * RumResourceEvent의 네트워크 phase timing/status_code를 기준으로 네트워크 카테고리 분류
 * @param resource RumResourceEvent['resource']
 * @returns 카테고리 (정상 응답 + 임계치 이내면 'ok')
 */
export const classifyResource = (resource: RumResourceEvent['resource']): NetworkCategory => {
  const { status_code, connect, dns, ssl, first_byte, download } = resource;

  if (typeof status_code === 'number') {
    if (status_code >= 500) return NETWORK_CATEGORY.HTTP_5XX;
    if (status_code >= 400) return NETWORK_CATEGORY.HTTP_4XX;
  }
  if (dns && dns.duration > msToNs(DNS_FAILURE_MS)) return NETWORK_CATEGORY.DNS_FAILURE;
  // TLS 실패: SSL phase 기록은 있지만 duration이 0에 수렴하고 connect도 사실상 미완료
  if (ssl && ssl.duration === 0 && connect?.duration === 0) return NETWORK_CATEGORY.TLS_ERROR;
  if (connect && connect.duration > msToNs(CONNECT_EXHAUSTION_MS))
    return NETWORK_CATEGORY.CONNECT_EXHAUSTION;
  if (first_byte && first_byte.duration > msToNs(SERVER_SLOW_MS))
    return NETWORK_CATEGORY.SERVER_SLOW;
  if (download && download.duration > msToNs(SERVER_SLOW_MS)) return NETWORK_CATEGORY.DOWNLOAD_SLOW;
  return NETWORK_CATEGORY.OK;
};

/**
 * 네트워크 에러 메시지 기반 카테고리 매칭
 * - 기존 Phase 2 드롭 패턴 중 "원인 구분이 가능한" 것들을 태깅 대상으로 이동
 * @param message 에러 메시지
 * @returns 매칭되는 네트워크 카테고리 또는 null (네트워크 에러가 아니면)
 */
export const classifyNetworkError = (message = ''): NetworkCategory | null => {
  if (/Failed to fetch|Network Error|NetworkError|Load failed|Network request failed/.test(message))
    return NETWORK_CATEGORY.OFFLINE;
  if (/AbortError|canceled|The user aborted a request/.test(message))
    return NETWORK_CATEGORY.CLIENT_ABORT;
  if (/timeout of|TimeoutError|signal timed out/.test(message)) return NETWORK_CATEGORY.SERVER_SLOW;
  return null;
};

/** Phase 4: 서드파티/보안/브라우저 노이즈 */
const IGNORE_MESSAGE_PATTERNS_PHASE4 = [
  '__gCrWeb',
  'instantSearchSDKJSBridgeClearHighlight',
  'top.GLOBALS',
  "can't access dead object",
  'SecurityError: DOM Exception 18',
  "SecurityError: Failed to execute 'replaceState'",
  'Permission denied to access property',
  'Java object is gone',
  'connectpayWebBridge',
  'kakaoPixel is not defined',
  "Cannot read properties of null (reading 'getItem')",
  "Cannot read properties of null (reading 'setItem')",
  "Cannot read properties of null (reading 'removeItem')",
  "null is not an object (evaluating 'window.localStorage",
  'Blocked a frame with origin',
] as const;

const IGNORE_MESSAGE_PATTERNS = [
  ...IGNORE_MESSAGE_PATTERNS_PHASE1,
  ...IGNORE_MESSAGE_PATTERNS_PHASE2,
  ...IGNORE_MESSAGE_PATTERNS_PHASE4,
] as const;

/**
 * NOTE Datadog RUM에 전달할 에러 중 무시할 에러 메시지 필터링
 * @param message 에러 메시지 (string)
 * @returns 무시해야 하면 true
 */
export const ignoreMessage = (message = ''): boolean =>
  IGNORE_MESSAGE_PATTERNS.some((pattern) => message.includes(pattern));

/** 브라우저 확장 및 3rd-party SDK 스택 트레이스 패턴 */
const IGNORE_STACK_PATTERNS = [
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
  'safari-web-extension://',
  'pinimg.com',
] as const;

/**
 * 스택 트레이스에 브라우저 확장/3rd-party SDK 패턴이 포함되었는지 확인
 * @param stack 에러 스택 트레이스
 * @returns 무시해야 하면 true
 */
export const ignoreStack = (stack = ''): boolean =>
  IGNORE_STACK_PATTERNS.some((pattern) => stack.includes(pattern));

/** ChunkLoadError 메시지 패턴 */
const CHUNK_LOAD_PATTERNS = ['ChunkLoadError', 'Loading chunk', 'Loading CSS chunk'] as const;

/**
 * ChunkLoadError 여부 확인 (드롭하지 않고 태깅용)
 * @param message 에러 메시지
 * @param type 에러 타입
 * @returns ChunkLoadError이면 true
 */
export const isChunkLoadError = (message = '', type = ''): boolean =>
  type === 'ChunkLoadError' || CHUNK_LOAD_PATTERNS.some((pattern) => message.includes(pattern));

const BOT_UA_PATTERN = /bot|crawl|spider|slurp|bingbot|googlebot|yandex|baidu|HeadlessChrome|Yeti/i;

/**
 * 봇/크롤러 User-Agent 여부 확인
 * @returns 봇이면 true
 */
export const isBotUserAgent = (): boolean => {
  try {
    return BOT_UA_PATTERN.test(navigator?.userAgent ?? '');
  } catch {
    return false;
  }
};

/**
 * 현재 RUM 세션/뷰 상관관계 컨텍스트 조회 (공개 유틸)
 * - 호출 측(api-client, React ErrorBoundary 등)이 백엔드 로그/APM 상관조회 시 사용
 * - trace_id는 resource 이벤트 시점에만 SDK 내부에 보관되므로 여기선 세션/뷰 식별자만 제공
 */
export const getCurrentTraceContext = ():
  | {
      sessionId: string | undefined;
      viewId: string | undefined;
      applicationId: string;
    }
  | undefined => {
  const ic = datadogRum.getInternalContext?.();
  if (!ic) return undefined;
  return {
    sessionId: ic.session_id,
    viewId: ic.view?.id,
    applicationId: ic.application_id,
  };
};

/**
 * beforeSend 내부에서 모든 이벤트에 병합할 상관관계 context 생성
 * - 모든 이벤트: sessionId / viewId
 * - resource 이벤트: + traceId / spanId (event._dd에서 추출 — 해당 요청에 주입된 trace)
 */
const buildCorrelationContext = (event: RumEvent): Record<string, string | undefined> => {
  const ic = datadogRum.getInternalContext?.();
  const base = {
    sessionId: ic?.session_id,
    viewId: ic?.view?.id,
  };
  if (event.type === 'resource') {
    const dd = (event as RumResourceEvent)._dd;
    return {
      ...base,
      traceId: dd?.trace_id,
      spanId: dd?.span_id,
    };
  }
  return base;
};

/**
 * NOTE datadogRum init 설정 유틸 함수
 * @param service 서비스명
 * @param version 버전
 * @param applicationId 애플리케이션 ID
 * @param clientToken 클라이언트 토큰
 */
export const datadogRumInit = ({
  service,
  version,
  applicationId,
  clientToken,
  team = DATA_DOG_TEAMS.DISPLAY,
  isELT = false,
}: {
  service: DataDogService;
  version: string;
  applicationId: string;
  clientToken: string;
  team?: DataDogTeam;
  isELT?: boolean;
}) => {
  const isLocal = import.meta.env.VITE_APP_MODE === 'local';

  if (!isLocal) {
    datadogRum.init({
      service,
      version,
      applicationId,
      clientToken,
      site: DATA_DOG_SITE,
      env: import.meta.env.VITE_APP_MODE,
      sessionSampleRate: isELT
        ? 100
        : window?.__MSS_FE__?.config?.dataDog?.sessionSampleRate ?? 0.1,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
      allowedTracingUrls: [/https:\/\/*\.musinsa\.com/],
      /* eslint-disable no-param-reassign -- Datadog beforeSend API는 event 직접 변이가 의도된 설계 */
      beforeSend: (event) => {
        // 0. Bot/크롤러 트래픽 전체 드롭
        if (isBotUserAgent()) return false;

        // 모든 수집 이벤트에 공통으로 붙일 상관관계 context (session/view, resource의 경우 trace/span)
        const correlationContext = buildCorrelationContext(event);

        if (event.type === 'resource') {
          const networkCategory = classifyResource(event.resource);
          event.context = {
            ...event.context,
            ...BASE_CONTEXT,
            ...correlationContext,
            service,
            source: DATA_DOG_SOURCES.RUM_INIT,
            networkCategory,
          };
          return true;
        }

        if (event.type === 'error') {
          const { message, stack, type: errorType } = event.error;

          // 1. 스택 기반 필터링 (브라우저 확장 + 3rd-party) — 최상단으로 이동: 앱 무관 노이즈 조기 드롭
          if (ignoreStack(stack)) return false;
          // 2. 네트워크 에러 태깅 (드롭 대신 networkCategory 부여하여 수집)
          const networkCategory = classifyNetworkError(message);
          if (networkCategory) {
            event.context = {
              ...event.context,
              ...BASE_CONTEXT,
              ...correlationContext,
              service,
              kind: errorType,
              message,
              stack,
              source: DATA_DOG_SOURCES.RUM_INIT,
              errorCategory: 'network',
              networkCategory,
            };
            return true;
          }
          // 3. 순수 노이즈 메시지 드롭 (Phase 1/2/4)
          if (ignoreMessage(message)) return false;
          // 4. ChunkLoadError 태깅 (드롭하지 않음)
          if (isChunkLoadError(message, errorType)) {
            event.context = {
              ...event.context,
              ...BASE_CONTEXT,
              ...correlationContext,
              service,
              kind: errorType,
              message,
              stack,
              source: DATA_DOG_SOURCES.RUM_INIT,
              errorCategory: 'chunk-load',
            };
            return true;
          }
          // 5. 일반 에러 — context 추가 후 수집
          event.context = {
            ...event.context,
            ...BASE_CONTEXT,
            ...correlationContext,
            service,
            kind: errorType,
            message,
            stack,
            source: DATA_DOG_SOURCES.RUM_INIT,
          };
          return true;
        }
        return true;
      },
      /* eslint-enable no-param-reassign */
    });
    datadogRum.setGlobalContextProperty('group', `${BASE_CONTEXT.group}`);
    datadogRum.setGlobalContextProperty('team', team);

    if (isELT) {
      datadogRum.setGlobalContextProperty('isELT', true);
    }
  }
};

/**
 * NOTE datadogRum addError 설정 유틸 함수
 * @param error 에러 객체
 * @param context 에러 발생 시 추가 정보 객체 (eg. kind, message)
 */
export const datadogRumAddError = ({
  error,
  context = {},
}: {
  error: unknown;
  context?: Record<string, unknown>;
}) => {
  const isLocal = import.meta.env.VITE_APP_MODE === 'local';
  if (isLocal || typeof datadogRum?.addError !== 'function') return;

  // Bot 트래픽 드롭
  if (isBotUserAgent()) return;

  if (error instanceof Error) {
    // 1. 스택 기반 필터링 (브라우저 확장 + 3rd-party) — 가장 먼저 드롭
    if (ignoreStack(error.stack)) return;

    // 2. 네트워크 에러는 드롭하지 않고 networkCategory로 태깅
    const networkCategory = classifyNetworkError(error.message);
    // 3. 네트워크 에러가 아니면 순수 노이즈 메시지 드롭
    if (!networkCategory && ignoreMessage(error.message)) return;

    const traceCtx = getCurrentTraceContext();
    const errorContext: Record<string, unknown> = {
      ...BASE_CONTEXT,
      sessionId: traceCtx?.sessionId,
      viewId: traceCtx?.viewId,
      kind: error.name,
      message: error.message ?? String(error),
      stack: error.stack,
      source: DATA_DOG_SOURCES.RUM_ADD_ERROR,
      ...context,
    };

    if (networkCategory) {
      errorContext.errorCategory = 'network';
      errorContext.networkCategory = networkCategory;
    } else if (isChunkLoadError(error.message, error.name)) {
      errorContext.errorCategory = 'chunk-load';
    }

    datadogRum.addError(error, errorContext);
  }
};
